import React, { useState, useMemo, useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useParams } from 'react-router-dom';
import ApiService from '../../../../../services/api.service';
import axios from '../../../../../services/http.service';
import randomId from '../../../../../utils/randomId';
import GrowPageContext from '../../contexts/GrowPageContext';
import ErrorsModal from './ErrorsModal';
import SkillTreeFooter from './SkillTreeFooter';
import SkillTreeNode from './SkillTreeNode';

function SkillTreeView({
  type,
  treeData,
  setTreeData,
  setInitialTreeData,
  initialTreeData,
  saved,
  setSaved,
  errorMessage,
  setErrorMessage,
  foundedSkills,
  growLevels,
}) {
  const { department } = useParams();
  const { setIsDialogOpened } = useContext(GrowPageContext);

  const [alertMessage, setAlertMessage] = useState('');
  const [sendRequest, setSendRequest] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [added, setAdded] = useState([]);
  const [updated, setUpdated] = useState([]);
  const [isDetailActive, setIsDetailActive] = useState(true);
  const [errors, setErrors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (foundedSkills.length > 0) {
      setExpanded((prevExpanded) => {
        return foundedSkills.includes(prevExpanded)
          ? prevExpanded
          : [...prevExpanded, ...foundedSkills];
      });
    }
  }, [foundedSkills]);

  const addTreeNode = useCallback(
    (arr, id) => {
      const childId = randomId();
      const updatedTree = arr.map((item) =>
        item.itemId === id
          ? {
              ...item,
              children: [...item.children, childId],
              details: [],
            }
          : item,
      );

      const newSkill = {
        itemId: childId,
        name: '',
        details: [
          {
            itemId: randomId(),
            name: '',
            successCriteria: '',
            materialName: '',
            materialType: '',
            link: '',
            level: growLevels[0]?._id,
            contentType: null,
          },
        ],
        children: [],
      };

      setAdded([...added, newSkill].filter((elem) => elem !== id));
      setExpanded(id ? [...expanded, childId, id] : [...expanded, childId]);

      return [...updatedTree, { ...newSkill }];
    },
    [expanded, growLevels, added, setAdded],
  );

  const findNodesToRemoving = useCallback((arr, id) => {
    const selectedItem = arr.find((item) => item.itemId === id);
    let resArr = [id];

    if (selectedItem?.children.length) {
      const nodeChildren = arr
        .filter((item) => selectedItem.children.includes(item.itemId))
        .map((item) => findNodesToRemoving(arr, item.itemId));
      resArr = [...resArr, ...nodeChildren];
    }

    return resArr.flat(Infinity);
  }, []);

  const removeTreeNode = useCallback(
    (arr, id) => {
      const rmNodes = findNodesToRemoving(arr, id);
      const foundItem = arr.find((item) => item.itemId === id);

      if (foundItem) {
        setRemoved([...removed, foundItem]);
      }

      setExpanded(expanded.filter((item) => !rmNodes.includes(item)));

      return arr
        .map((node) => {
          const children = node.children.filter((item) => item !== id);

          return {
            ...node,
            children,
            ...(!isEqual(node.children, children) && !children.length
              ? {
                  details: [
                    {
                      itemId: randomId(),
                      name: '',
                      successCriteria: '',
                      materialName: '',
                      materialType: '',
                      link: '',
                      level: growLevels[0]?._id,
                    },
                  ],
                }
              : {}),
          };
        })
        .filter((item) => !rmNodes.includes(item.itemId));
    },
    [expanded, findNodesToRemoving, growLevels, removed],
  );

  const updateTreeNode = (arr, id, updatedValue) => {
    const foundItem = arr.find((item) => item.itemId === id && item._id);

    if (foundItem) {
      setUpdated([...new Set([...updated, foundItem.itemId])]);
    }

    return arr.map((node) =>
      node.itemId === id
        ? {
            ...node,
            ...updatedValue,
          }
        : node,
    );
  };

  const validateFields = (arr) => {
    let error = false;

    arr.forEach((item) => {
      const { name, details } = item;
      if (!name.trim()) {
        error = true;
        setAlertMessage('All fields are required!');
      }

      details.forEach((detail) => {
        if (
          !detail.successCriteria.trim() ||
          !detail.materialName.trim() ||
          !detail.materialType.trim() ||
          !detail.link.trim()
        ) {
          error = true;
          setAlertMessage('All fields are required!');
        }
      });
    });

    return error;
  };

  const prepareData = (array) =>
    array.map((node) => ({
      ...node,
      name: node.name.trim(),
      details: node.details.map(({ contentType, ...detail }) => ({
        ...detail,
        name: detail.name.trim(),
        successCriteria: detail.successCriteria.trim(),
        materialName: detail.materialName.trim(),
        materialType: detail.materialType.trim(),
        link: detail.link.trim(),
        contentType,
      })),
    }));

  const handleSave = () => {
    try {
      const isError = validateFields(treeData);

      if (isError) {
        return false;
      }

      setSendRequest(true);

      axios({
        method: 'POST',
        url: 'head/grow',
        data: {
          treeData: prepareData(treeData),
          department,
          skillType: type,
        },
      }).then((response) => {
        setInitialTreeData(response);
        setTreeData(response);
        setSendRequest(false);
        setSaved(true);
      });
    } catch (error) {
      setSendRequest(false);
      setErrorMessage({ isOpen: true, message: `ERROR: ${error.message}` });
    }

    return true;
  };

  const sendJson = async (file) => {
    try {
      setSendRequest(true);

      const formData = new FormData();
      formData.append('file', file);

      const result = await ApiService.head.sendJson({
        department,
        skillType: type,
        body: formData,
      });

      const addedResult = result.jsonData.filter((task) => !task.createdAt);

      setTreeData(result.jsonData);
      setAdded(addedResult);
      setSendRequest(false);

      if (result.errors.length) {
        setErrors(result.errors);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error(error);
      setSendRequest(false);
    }
  };

  const sendCsv = async (file) => {
    try {
      setSendRequest(true);

      const formData = new FormData();
      formData.append('file', file);
      const result = await ApiService.head.sendCsv({ department, skillType: type, body: formData });

      const addedResult = result.csvData.filter((task) => !task.createdAt);

      setTreeData(result.csvData);
      setAdded(addedResult);
      setSendRequest(false);

      if (result.errors.length) {
        setErrors(result.errors);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error(error);
      setAlertMessage(error.response?.data?.messages || 'Import failed');
      setSendRequest(false);
    }
  };

  const filteredTree = useMemo(
    () => treeData.filter((i) => !treeData.some((item) => item.children.includes(i.itemId))),
    [treeData],
  );

  // Count unique tasks (details) across all nodes
  const uniqueTasksCount = useMemo(() => {
    const taskNames = new Set();
    treeData.forEach((node) => {
      node.details.forEach((detail) => {
        if (detail.name) {
          taskNames.add(detail.name);
        }
      });
    });
    return taskNames.size;
  }, [treeData]);

  // Total tasks count (including duplicates)
  const totalTasksCount = useMemo(() => {
    return treeData.reduce((acc, node) => acc + node.details.length, 0);
  }, [treeData]);

  const toggleExpand = (id) => {
    setExpanded(expanded.includes(id) ? expanded.filter((item) => item !== id) : [...expanded, id]);
  };

  return (
    <div className="space-y-4">
      {/* Stats Header */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">{filteredTree.length}</span> skill
          {filteredTree.length !== 1 ? 's' : ''}
        </span>
        <span className="text-border">•</span>
        <span>
          <span className="font-medium text-foreground">{uniqueTasksCount}</span> unique task
          {uniqueTasksCount !== 1 ? 's' : ''}
        </span>
        {totalTasksCount !== uniqueTasksCount && (
          <>
            <span className="text-border">•</span>
            <span>
              <span className="font-medium text-foreground">{totalTasksCount}</span> total
            </span>
          </>
        )}
      </div>

      {/* Tree View */}
      <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-2">
        {filteredTree.map((node) => (
          <SkillTreeNode
            key={node.itemId}
            node={node}
            treeData={treeData}
            updateTreeNode={(id, newValue) => setTreeData(updateTreeNode(treeData, id, newValue))}
            addTreeNode={(id) => setTreeData(addTreeNode(treeData, id))}
            removeTreeNode={(id) => setTreeData(removeTreeNode(treeData, id))}
            growLevels={growLevels}
            expanded={expanded}
            toggleExpand={toggleExpand}
            isDetailActive={isDetailActive}
            setIsDetailActive={setIsDetailActive}
          />
        ))}

        {filteredTree.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No skills yet. Click &quot;Add Skill&quot; to create your first skill.
          </div>
        )}
      </div>

      {/* Footer */}
      <SkillTreeFooter
        type={type}
        onAddSkill={() => setTreeData(addTreeNode(treeData, null))}
        onSave={handleSave}
        disabledSave={sendRequest || (!added.length && !updated.length && !removed.length)}
        disabledExport={!treeData.length || sendRequest}
        disabledImport={sendRequest}
        sendJson={sendJson}
        sendCsv={sendCsv}
        removed={removed}
        added={added}
        updated={updated}
        treeSkills={treeData}
        initialTreeData={initialTreeData}
        clearData={() => {
          setAdded([]);
          setRemoved([]);
          setUpdated([]);
        }}
        setTreeData={setTreeData}
        setAdded={setAdded}
        setAlertMessage={setAlertMessage}
        validateFields={validateFields}
        setIsDialogOpened={setIsDialogOpened}
      />

      {/* Errors Modal */}
      <ErrorsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        errors={errors}
        setIsDialogOpened={setIsDialogOpened}
      />

      {/* Alert Message */}
      {alertMessage && (
        <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg">
          {alertMessage}
          <button type="button" onClick={() => setAlertMessage('')} className="ml-2 font-bold">
            ×
          </button>
        </div>
      )}
    </div>
  );
}

SkillTreeView.propTypes = {
  type: PropTypes.string.isRequired,
  treeData: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      name: PropTypes.string,
      details: PropTypes.arrayOf(
        PropTypes.shape({
          itemId: PropTypes.string,
          successCriteria: PropTypes.string,
          materialName: PropTypes.string,
          materialType: PropTypes.string,
          link: PropTypes.string,
          level: PropTypes.string,
        }),
      ),
      children: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  foundedSkills: PropTypes.arrayOf(PropTypes.string).isRequired,
  setTreeData: PropTypes.func.isRequired,
  initialTreeData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setInitialTreeData: PropTypes.func.isRequired,
  saved: PropTypes.bool.isRequired,
  setSaved: PropTypes.func.isRequired,
  errorMessage: PropTypes.shape({
    isOpen: PropTypes.bool,
    message: PropTypes.string,
  }).isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  growLevels: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      key: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default SkillTreeView;
